import { AssertError } from '../errors/internal-error';
import { ComponentItem } from '../items/component-item';
import { LayoutManager } from '../layout-manager';
import { DomConstants } from '../utils/dom-constants';
import { DragListener } from '../utils/drag-listener';
import { numberToPixels, pixelsToNumber } from '../utils/utils';
import { Tab } from './tab';

/** @internal */
export type TabsContainerComponentItemRemoveEvent = (
  this: void,
  componentItem: ComponentItem,
) => void;
/** @internal */
export type TabsContainerComponentItemFocusEvent = (
  this: void,
  componentItem: ComponentItem,
) => void;
/** @internal */
export type TabsContainerComponentItemDragStartEvent = (
  this: void,
  x: number,
  y: number,
  dragListener: DragListener,
  componentItem: ComponentItem,
) => void;
/** @internal */
export type TabsContainerDropdownActiveChangedEvent = (this: void) => void;

/** @internal */
export class TabsContainer {
  // There is one tab per ComponentItem in stack.  However they may not be ordered the same
  private readonly _tabs: Tab[] = [];

  private readonly _dropdownElement: HTMLElement;
  private readonly _element: HTMLElement;
  private _lastVisibleTabIndex = -1;
  private _dropdownActive = false;

  get tabs(): Tab[] {
    return this._tabs;
  }
  get tabCount(): number {
    return this._tabs.length;
  }
  get lastVisibleTabIndex(): number {
    return this._lastVisibleTabIndex;
  }

  get element(): HTMLElement {
    return this._element;
  }
  get dropdownElement(): HTMLElement {
    return this._dropdownElement;
  }
  get dropdownActive(): boolean {
    return this._dropdownActive;
  }

  constructor(
    private readonly _layoutManager: LayoutManager,
    private readonly _componentRemoveEvent: TabsContainerComponentItemRemoveEvent,
    private readonly _componentFocusEvent: TabsContainerComponentItemFocusEvent,
    private readonly _componentDragStartEvent: TabsContainerComponentItemDragStartEvent,
    private readonly _dropdownActiveChangedEvent: TabsContainerDropdownActiveChangedEvent,
  ) {
    this._element = document.createElement('section');
    this._element.classList.add(DomConstants.ClassName.Tabs);

    this._dropdownElement = document.createElement('section');
    this._dropdownElement.classList.add(DomConstants.ClassName.TabDropdownList);
    this._dropdownElement.style.display = 'none';
  }

  destroy(): void {
    for (const tab of this._tabs) {
      tab.destroy();
    }
  }

  /**
   * Creates a new tab and associates it with a contentItem
   * @param index - The position of the tab
   */
  createTab(componentItem: ComponentItem, index: number): Tab | undefined {
    //If there's already a tab relating to the
    //content item, don't do anything
    for (const tab of this._tabs) {
      if (tab.componentItem === componentItem) {
        return undefined;
      }
    }

    const tab = new Tab(
      this._layoutManager,
      componentItem,
      (item) => this.handleTabCloseEvent(item),
      (item) => this.handleTabFocusEvent(item),
      (x, y, dragListener, item) =>
        this.handleTabDragStartEvent(x, y, dragListener, item),
    );

    if (index === undefined) {
      index = this._tabs.length;
    }

    this._tabs.splice(index, 0, tab);

    if (index < this._element.childNodes.length) {
      this._element.insertBefore(tab.element, this._element.childNodes[index]);
    } else {
      this._element.appendChild(tab.element);
    }

    return tab;
  }

  removeTab(componentItem: ComponentItem): void {
    // componentItem cannot be ActiveComponentItem
    for (let i = 0; i < this._tabs.length; i++) {
      if (this._tabs[i].componentItem === componentItem) {
        const tab = this._tabs[i];
        tab.destroy();
        this._tabs.splice(i, 1);
        if (i <= this._lastVisibleTabIndex) {
          this._lastVisibleTabIndex--;
        }
        return;
      }
    }

    throw new Error('contentItem is not controlled by this header');
  }

  processActiveComponentChanged(newActiveComponentItem: ComponentItem): void {
    let activeIndex = -1;
    for (let i = 0; i < this._tabs.length; i++) {
      const isActive = this._tabs[i].componentItem === newActiveComponentItem;
      this._tabs[i].setActive(isActive);
      if (isActive) {
        activeIndex = i;
      }
    }

    if (activeIndex < 0) {
      throw new AssertError('HSACI56632');
    } else if (
      this._layoutManager.layoutConfig.settings.reorderOnTabMenuClick
    ) {
      /**
       * If the tab selected was in the dropdown, move everything down one to make way for this one to be the first.
       * This will make sure the most used tabs stay visible.
       */
      const activeTab = this._tabs[activeIndex];
      if (activeTab.element.parentElement === this._dropdownElement) {
        for (let j = activeIndex; j > 0; j--) {
          this._tabs[j] = this._tabs[j - 1];
        }
        this._tabs[0] = activeTab;
        this._element.prepend(activeTab.element);

        // updateTabSizes will move overflowed tabs between their containers.
      }
    }
  }

  /**
   * Pushes the tabs to the tab dropdown if the available space is not sufficient
   */
  updateTabSizes(
    availableWidth: number,
    activeComponentItem: ComponentItem | undefined,
  ): void {
    let dropDownActive = false;
    const success = this.tryUpdateTabSizes(
      dropDownActive,
      availableWidth,
      activeComponentItem,
    );
    if (!success) {
      dropDownActive = true;
      // this will always succeed
      this.tryUpdateTabSizes(
        dropDownActive,
        availableWidth,
        activeComponentItem,
      );
    }

    if (dropDownActive !== this._dropdownActive) {
      this._dropdownActive = dropDownActive;
      this._dropdownActiveChangedEvent();
    }
  }

  tryUpdateTabSizes(
    dropdownActive: boolean,
    availableWidth: number,
    activeComponentItem: ComponentItem | undefined,
  ): boolean {
    if (this._tabs.length === 0) {
      return true;
    }
    if (activeComponentItem === undefined) {
      throw new Error('non-empty tabs must have active component item');
    }

    let cumulativeTabWidth = 0;
    const overlapState = { exceeded: false };
    const tabOverlapAllowance =
      this._layoutManager.layoutConfig.settings.tabOverlapAllowance;
    const activeIndex = this._tabs.indexOf(activeComponentItem.tab);
    const activeTab = this._tabs[activeIndex];
    this._lastVisibleTabIndex = -1;

    for (let i = 0; i < this._tabs.length; i++) {
      const tabElement = this._tabs[i].element;
      if (tabElement.parentElement !== this._element) {
        this._element.appendChild(tabElement);
      }
      const tabMarginRight = pixelsToNumber(
        getComputedStyle(activeTab.element).marginRight,
      );
      cumulativeTabWidth += tabElement.offsetWidth + tabMarginRight;
      const visibleTabWidth = this.getVisibleTabWidth(
        i,
        cumulativeTabWidth,
        activeIndex,
        activeTab,
      );

      if (
        Number.isNaN(visibleTabWidth) ||
        Number.isNaN(availableWidth) ||
        visibleTabWidth <= availableWidth
      ) {
        this._lastVisibleTabIndex = i;
        this.resetTabStyleAndMove(tabElement, this._element);
      } else if (
        !this.placeOverflowTab(
          i,
          visibleTabWidth,
          availableWidth,
          activeIndex,
          tabOverlapAllowance,
          dropdownActive,
          overlapState,
        )
      ) {
        return false;
      }
    }

    return true;
  }

  private getVisibleTabWidth(
    index: number,
    cumulativeTabWidth: number,
    activeIndex: number,
    activeTab: Tab,
  ): number {
    if (activeIndex <= index) {
      return cumulativeTabWidth;
    }
    const activeTabMarginRight = pixelsToNumber(
      getComputedStyle(activeTab.element).marginRight,
    );
    return (
      cumulativeTabWidth + activeTab.element.offsetWidth + activeTabMarginRight
    );
  }

  private placeOverflowTab(
    index: number,
    visibleTabWidth: number,
    availableWidth: number,
    activeIndex: number,
    tabOverlapAllowance: number,
    dropdownActive: boolean,
    overlapState: { exceeded: boolean },
  ): boolean {
    const tabElement = this._tabs[index].element;
    if (!overlapState.exceeded) {
      if (
        this.tryApplyTabOverlap(
          index,
          visibleTabWidth,
          availableWidth,
          activeIndex,
          tabOverlapAllowance,
        )
      ) {
        return true;
      }
      overlapState.exceeded = true;
    } else if (index === activeIndex) {
      this.resetTabStyleAndMove(tabElement, this._element);
    }

    if (overlapState.exceeded && index !== activeIndex) {
      if (!dropdownActive) {
        return false;
      }
      this.resetTabStyleAndMove(tabElement, this._dropdownElement);
    }
    return true;
  }

  private tryApplyTabOverlap(
    index: number,
    visibleTabWidth: number,
    availableWidth: number,
    activeIndex: number,
    tabOverlapAllowance: number,
  ): boolean {
    const excessWidth = visibleTabWidth - availableWidth;
    let overlap = 0;
    if (activeIndex > 0 && activeIndex <= index) {
      if (index > 1) {
        overlap = excessWidth / (index - 1);
      }
    } else if (index > 0) {
      overlap = excessWidth / index;
    }
    if (
      Number.isNaN(overlap) ||
      Number.isNaN(tabOverlapAllowance) ||
      overlap >= tabOverlapAllowance
    ) {
      return false;
    }
    for (let j = 0; j <= index; j++) {
      const marginLeft =
        j !== activeIndex && j !== 0 ? '-' + numberToPixels(overlap) : '';
      this._tabs[j].element.style.zIndex = String(index - j);
      this._tabs[j].element.style.marginLeft = marginLeft;
    }
    this._lastVisibleTabIndex = index;
    const tabElement = this._tabs[index].element;
    if (tabElement.parentElement !== this._element) {
      this._element.appendChild(tabElement);
    }
    return true;
  }

  private resetTabStyleAndMove(
    tabElement: HTMLElement,
    target: HTMLElement,
  ): void {
    tabElement.style.zIndex = 'auto';
    tabElement.style.marginLeft = '';
    if (tabElement.parentElement !== target) {
      target.appendChild(tabElement);
    }
  }

  /**
   * Shows drop down for additional tabs when there are too many to display.
   */
  showAdditionalTabsDropdown(): void {
    this._dropdownElement.style.display = '';
  }

  /**
   * Hides drop down for additional tabs when there are too many to display.
   */
  hideAdditionalTabsDropdown(): void {
    this._dropdownElement.style.display = 'none';
  }

  private handleTabCloseEvent(componentItem: ComponentItem) {
    this._componentRemoveEvent(componentItem);
  }

  private handleTabFocusEvent(componentItem: ComponentItem) {
    this._componentFocusEvent(componentItem);
  }

  private handleTabDragStartEvent(
    x: number,
    y: number,
    dragListener: DragListener,
    componentItem: ComponentItem,
  ) {
    this._componentDragStartEvent(x, y, dragListener, componentItem);
  }
}
